import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { WE_RIDER_LOGO_URL as weRiderLogo } from "../../services/local/institutionalBranding";
import mercadoPagoLogo from "../../assets/branding/mercado-pago-logo.png";
import useWePedidosBranding from "../we-pedidos/useWePedidosBranding";
import WeRiderAccess from "../we-pedidos/WeRiderAccess";
import WeRiderAndroidLauncher from "../we-pedidos/WeRiderAndroidLauncher";
import WeRiderIdentityOnboarding from "../we-pedidos/WeRiderIdentityOnboarding";
import {
  configureNativeRiderBackground,
  notifyRiderDashboardChanges,
  stopNativeRiderBackground,
} from "../../services/local/weAppNotifications";
import {
  getWeRiderEligibilityCopy,
  normalizeWeRiderCompliance,
} from "../../services/local/weRiderCompliance";
import {
  advanceWeRiderJob,
  claimWeRiderJob,
  getWeRiderAuthState,
  loadWeRiderDashboard,
  registerWeRiderBackgroundDevice,
  registerWeRiderProfile,
  revokeWeRiderBackgroundDevice,
  setWeRiderAvailability,
  signOutWeRider,
  subscribeToWeRiderDashboardChanges,
  subscribeToWeRiderAuth,
  submitWeRiderIdentity,
  updateWeRiderLocation,
  verifyWeRiderDeliveryCode,
  verifyWeRiderPickupCode,
} from "../../services/supabase/weRiderService";
import {
  approveLocalWeRiderPayment,
  createWeRiderCollectionQr,
  createWeRiderPickupDeeplink,
  quoteWeRiderPickupPayment,
  disconnectWeRiderMercadoPago,
  getWeRiderPaymentConnection,
  isLocalWeRiderPaymentsPreview,
  recordWeRiderCollectionCash,
  recordWeRiderPickupCash,
  recordWeRiderRestaurantDeliveryFeeCash,
  refreshWeRiderPaymentOperation,
  startWeRiderMercadoPagoConnection,
} from "../../services/supabase/weRiderPaymentsService";
import "../../styles/WeRider.css";

const moneyFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 0,
});

const timeFormatter = new Intl.DateTimeFormat("es-AR", {
  hour: "2-digit",
  minute: "2-digit",
});

const VEHICLE_LABELS = {
  motorcycle: "Moto",
  bicycle: "Bicicleta",
  scooter: "Scooter / monopatín",
  car: "Auto",
  walking: "A pie",
  other: "Otro medio sin licencia",
};

const JOB_STATUS = {
  searching_driver: { label: "Buscando rider", step: 0, tone: "waiting" },
  driver_assigned: { label: "Viaje aceptado", step: 1, tone: "assigned" },
  arriving_pickup: { label: "Yendo al restaurante", step: 2, tone: "moving" },
  ready_for_pickup: { label: "Listo para retirar", step: 2, tone: "ready" },
  arrived_pickup: { label: "En el restaurante", step: 2, tone: "ready" },
  picked_up: { label: "Pedido retirado", step: 3, tone: "picked" },
  in_transit: { label: "En camino", step: 4, tone: "moving" },
  delivered: { label: "Entregado", step: 5, tone: "delivered" },
  cancelled: { label: "Cancelado", step: 0, tone: "cancelled" },
};

const ACTIVE_ACTIONS = {
  driver_assigned: {
    label: "Voy al restaurante",
    nextStatus: "arriving_pickup",
  },
  arriving_pickup: {
    label: "Llegué al restaurante",
    nextStatus: "arrived_pickup",
  },
  ready_for_pickup: {
    label: "Llegué al restaurante",
    nextStatus: "arrived_pickup",
  },
  picked_up: {
    label: "Iniciar entrega",
    nextStatus: "in_transit",
  },
};

const PAYMENT_METHOD_LABELS = {
  cash: "Efectivo",
  cash_on_delivery: "Pagar al Rider",
  qr_on_delivery: "QR al entregar",
  card_on_delivery: "Tarjeta al entregar",
  mercado_pago: "Mercado Pago",
  transfer: "Transferencia",
  digital: "Pago digital",
  other: "Otro medio",
};

const COMPLIANCE_DETAIL_LABELS = {
  profile: {
    complete: "Completo",
    incomplete: "Incompleto",
  },
  identity: {
    not_started: "Sin iniciar",
    pending: "En revisión",
    approved: "Aprobada",
    action_required: "Requiere datos",
    rejected: "Rechazada",
    expired: "Vencida",
  },
  insurance: {
    not_started: "Sin iniciar",
    quote_pending: "Cotización pendiente",
    payment_pending: "Pago pendiente",
    issuance_pending: "Emisión pendiente",
    active: "Vigente",
    past_due: "Pago vencido",
    expired: "Vencido",
    cancelled: "Cancelado",
  },
  review: {
    pending: "Pendiente",
    approved: "Aprobada",
    action_required: "Requiere datos",
    rejected: "Rechazada",
    suspended: "Suspendida",
  },
};

const formatDistance = (distance) => {
  const value = Number(distance);
  if (!Number.isFinite(value)) return "Distancia a confirmar";
  if (value < 1) return `${Math.max(100, Math.round(value * 10) * 100)} m`;
  return `${value.toFixed(1).replace(".", ",")} km`;
};

export const buildMapsUrl = (place = {}) => {
  const hasCoordinate = (value) =>
    value !== null &&
    value !== undefined &&
    String(value).trim() !== "" &&
    Number.isFinite(Number(value));
  const latitude = Number(place.latitude);
  const longitude = Number(place.longitude);
  const destination =
    hasCoordinate(place.latitude) && hasCoordinate(place.longitude)
      ? `${latitude},${longitude}`
      : [place.address, place.city, place.province].filter(Boolean).join(", ");
  if (!destination) return "";
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
};

const getInitials = (fullName) =>
  String(fullName || "R")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "R";

const formatRemainingTime = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export function RiderComplianceSummary({ compliance }) {
  const normalized = normalizeWeRiderCompliance(compliance);
  const statusCopy = getWeRiderEligibilityCopy(normalized.eligibilityStatus);
  const details = [
    {
      label: "Perfil",
      value: COMPLIANCE_DETAIL_LABELS.profile[normalized.profileStatus],
    },
    {
      label: "Identidad",
      value: COMPLIANCE_DETAIL_LABELS.identity[normalized.identityStatus],
    },
    {
      label: "Seguro (opcional)",
      value: COMPLIANCE_DETAIL_LABELS.insurance[normalized.insuranceStatus],
    },
    {
      label: "Revisión",
      value: COMPLIANCE_DETAIL_LABELS.review[normalized.reviewStatus],
    },
  ];

  return (
    <section
      className={`we-rider-compliance is-${statusCopy.tone}`}
      aria-labelledby="we-rider-compliance-title"
    >
      <header>
        <div>
          <span className="we-rider-eyebrow">Habilitación del rider</span>
          <h2 id="we-rider-compliance-title">{statusCopy.label}</h2>
          <p>{normalized.statusReason || statusCopy.description}</p>
        </div>
        <strong>{normalized.canOperate ? "Puede operar" : "Pendiente"}</strong>
      </header>
      <div className="we-rider-compliance-grid">
        {details.map((detail) => (
          <div key={detail.label}>
            <span>{detail.label}</span>
            <strong>{detail.value || "Pendiente"}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

function RiderProfileCompletion({ email, onSaved }) {
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    vehicleType: "",
    vehicleReference: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const dashboard = await registerWeRiderProfile(form);
      onSaved(dashboard);
    } catch (requestError) {
      setError(requestError?.message || "No pudimos guardar tu perfil.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="we-rider-profile-shell">
      <section className="we-rider-profile-panel">
        <img src={weRiderLogo} alt="We! Rider" />
        <p className="we-rider-eyebrow">Último paso</p>
        <h1>Completa tu perfil de rider</h1>
        <p>{email}</p>
        <form className="we-rider-form" onSubmit={handleSubmit}>
          <label>
            Nombre y apellido
            <input
              value={form.fullName}
              onChange={(event) => setForm({ ...form, fullName: event.target.value })}
              required
            />
          </label>
          <label>
            Celular
            <input
              type="tel"
              inputMode="tel"
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
              required
            />
          </label>
          <label>
            Vehículo
            <select
              value={form.vehicleType}
              onChange={(event) => setForm({ ...form, vehicleType: event.target.value })}
              required
            >
              <option value="" disabled>Selecciona tu medio</option>
              <option value="motorcycle">Moto</option>
              <option value="bicycle">Bicicleta</option>
              <option value="scooter">Scooter / monopatín</option>
              <option value="car">Auto</option>
              <option value="walking">A pie</option>
              <option value="other">Otro medio sin licencia</option>
            </select>
          </label>
          <label>
            Identificación del vehículo
            <input
              value={form.vehicleReference}
              onChange={(event) =>
                setForm({ ...form, vehicleReference: event.target.value })
              }
              placeholder="Patente o color"
            />
          </label>
          {error ? <p className="we-rider-message is-error">{error}</p> : null}
          <button className="we-rider-primary" type="submit" disabled={saving}>
            {saving ? "Guardando..." : "Empezar"}
          </button>
        </form>
      </section>
    </main>
  );
}

function MapButton({ place, children = "Abrir mapa" }) {
  const url = buildMapsUrl(place);
  if (!url) return null;
  return (
    <a className="we-rider-map-button" href={url} target="_blank" rel="noreferrer">
      <span aria-hidden="true">↗</span>
      {children}
    </a>
  );
}

function JobItems({ items = [] }) {
  if (!items.length) return <span className="we-rider-muted">Sin detalle de productos</span>;
  return (
    <ul className="we-rider-items">
      {items.map((item) => (
        <li key={item.id || `${item.name}-${item.quantity}`}>
          <strong>{Number(item.quantity || 0)}x</strong>
          <span>{item.name}</span>
          {item.notes ? <small>{item.notes}</small> : null}
        </li>
      ))}
    </ul>
  );
}

function AvailableJobCard({ job, online, busy, onAccept }) {
  const status = JOB_STATUS[job.status] || JOB_STATUS.searching_driver;
  const itemCount = (job.items || []).reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );
  return (
    <article className="we-rider-job-card">
      <header>
        <div>
          <span className={`we-rider-status is-${status.tone}`}>{status.label}</span>
          <h3>{job.pickup?.name || "Restaurante"}</h3>
          <p>{job.pickup?.address || job.pickup?.city || "Punto de retiro"}</p>
        </div>
        <strong>{formatDistance(job.distanceToPickupKm)}</strong>
      </header>
      <div className="we-rider-job-route">
        <div>
          <span>Retiro</span>
          <strong>{job.pickup?.name || "Restaurante"}</strong>
        </div>
        <span className="we-rider-route-line" aria-hidden="true">→</span>
        <div>
          <span>Entrega</span>
          <strong>
            {job.destination?.neighborhood || job.destination?.city || "Zona informada"}
          </strong>
        </div>
      </div>
      <div className="we-rider-job-summary">
        <span>{itemCount} {itemCount === 1 ? "producto" : "productos"}</span>
        <span>{PAYMENT_METHOD_LABELS[job.payment?.method] || "Pago informado"}</span>
        {job.payment?.collectOnDelivery ? (
          <strong>Cobrar {moneyFormatter.format(job.payment?.amount || 0)}</strong>
        ) : (
          <strong className="is-paid">Pago confirmado</strong>
        )}
      </div>
      <footer>
        <span>Pedido {job.displayNumber}</span>
        <button
          className="we-rider-primary"
          type="button"
          disabled={!online || busy}
          onClick={() => onAccept(job.id)}
        >
          {busy ? "Asignando..." : online ? "Aceptar viaje" : "Ponte disponible"}
        </button>
      </footer>
    </article>
  );
}

function DeliveryProgress({ status }) {
  const currentStep = (JOB_STATUS[status] || JOB_STATUS.driver_assigned).step;
  const steps = ["Aceptado", "En retiro", "Retirado", "En camino", "Entregado"];
  return (
    <ol className="we-rider-progress" aria-label="Progreso de la entrega">
      {steps.map((label, index) => {
        const stepNumber = index + 1;
        const completed = currentStep >= stepNumber;
        const active = currentStep === stepNumber;
        return (
          <li key={label} className={`${completed ? "is-complete" : ""}${active ? " is-active" : ""}`}>
            <span>{completed && !active ? "✓" : stepNumber}</span>
            <small>{label}</small>
          </li>
        );
      })}
    </ol>
  );
}

const PAYMENT_OPERATION_STATUS = {
  created: "Creado",
  pending: "Esperando confirmacion",
  approved: "Confirmado",
  rejected: "Rechazado",
  cancelled: "Cancelado",
  expired: "Vencido",
  refunded: "Devuelto",
  error: "Con error",
};

export function RiderPaymentStep({
  kind,
  amount,
  operation,
  mercadoPagoConnected,
  busy,
  onCash,
  onDigital,
  onRefresh,
  onApproveLocal,
}) {
  const isPickup = kind === "pickup";
  const isRestaurantDeliveryFee = kind === "restaurant_delivery_fee";
  const approved = operation?.status === "approved";
  const pending = ["created", "pending"].includes(operation?.status);

  return (
    <section
      className={`we-rider-payment-step${approved ? " is-approved" : ""}`}
      aria-label={
        isRestaurantDeliveryFee
          ? "Cobro del envio al restaurante"
          : isPickup
            ? "Pago al restaurante"
            : "Cobro al cliente"
      }
    >
      <header>
        <div>
          <span>
            {isRestaurantDeliveryFee
              ? "En la caja del restaurante"
              : isPickup
                ? "Antes de retirar"
                : "Antes de entregar"}
          </span>
          <h3>
            {isRestaurantDeliveryFee
              ? "Cobra el envio"
              : isPickup
                ? "Paga al restaurante"
                : "Cobra al cliente"}
          </h3>
        </div>
        <strong>{moneyFormatter.format(amount || 0)}</strong>
      </header>

      {approved ? (
        <div className="we-rider-payment-approved" role="status">
          <span aria-hidden="true">✓</span>
          <div>
            <strong>
              {isRestaurantDeliveryFee
                ? "Envio cobrado al restaurante"
                : isPickup
                  ? "Pago al restaurante confirmado"
                  : "Cobro confirmado"}
            </strong>
            <small>
              {operation.method === "free"
                ? "Este pedido tiene envio sin cargo"
                : operation.method === "prepaid"
                ? "El pedido ya fue pagado antes de asignar la entrega"
                : operation.method === "cash"
                ? isRestaurantDeliveryFee
                  ? "Efectivo recibido en la caja del restaurante"
                  : "Registrado en efectivo"
                : isPickup
                  ? "Deeplink acreditado con comision MozzoApp"
                  : "QR acreditado directamente al Rider"}
            </small>
          </div>
        </div>
      ) : operation ? (
        <div className="we-rider-payment-pending">
          <div>
            <span>Estado</span>
            <strong>
              {PAYMENT_OPERATION_STATUS[operation.status] || operation.status}
            </strong>
          </div>
          {operation.method === "deeplink" && operation.checkoutUrl ? (
            <a
              className="we-rider-primary"
              href={operation.checkoutUrl}
              target="_blank"
              rel="noreferrer"
            >
              Abrir Mercado Pago
            </a>
          ) : null}
          {operation.method === "qr" && operation.qrData ? (
            <div className="we-rider-collection-qr">
              <QRCodeCanvas value={operation.qrData} size={220} level="M" />
              <strong>El cliente puede escanear con cualquier billetera</strong>
              <small>El importe se acredita en la cuenta Mercado Pago del Rider.</small>
            </div>
          ) : null}
          {pending ? (
            <div className="we-rider-payment-actions">
              <button type="button" onClick={onRefresh} disabled={busy}>
                Actualizar pago
              </button>
              {isLocalWeRiderPaymentsPreview() ? (
                <button type="button" onClick={onApproveLocal} disabled={busy}>
                  Confirmar prueba local
                </button>
              ) : null}
            </div>
          ) : (
            <button
              className="we-rider-secondary"
              type="button"
              onClick={onDigital || onCash}
              disabled={busy}
            >
              Intentar nuevamente
            </button>
          )}
        </div>
      ) : (
        <div
          className={`we-rider-payment-options${
            isRestaurantDeliveryFee ? " is-single" : ""
          }`}
        >
          <button type="button" onClick={onCash} disabled={busy}>
            <strong>
              {isRestaurantDeliveryFee ? "Cobrar en efectivo" : "Efectivo"}
            </strong>
            <span>
              {isRestaurantDeliveryFee
                ? "Registrar el envio recibido en caja"
                : isPickup
                  ? "Registrar pago al local"
                  : "Registrar cobro al cliente"}
            </span>
          </button>
          {!isRestaurantDeliveryFee ? (
            <button
              type="button"
              onClick={onDigital}
              disabled={busy || (!isPickup && !mercadoPagoConnected)}
            >
              <strong>{isPickup ? "Pagar con Mercado Pago" : "Cobrar con QR"}</strong>
              <span>
                {isPickup
                  ? "Abre un deeplink con comision MozzoApp"
                  : mercadoPagoConnected
                    ? "QR interoperable sin comision MozzoApp"
                    : "Conecta Mercado Pago para habilitarlo"}
              </span>
            </button>
          ) : null}
        </div>
      )}
    </section>
  );
}

function RiderMercadoPagoCard({ connection, busy, onConnect, onDisconnect }) {
  const connected = connection?.connected === true;
  return (
    <section className={`we-rider-mp-card${connected ? " is-connected" : ""}`}>
      <img
        className="we-rider-mp-mark"
        src={mercadoPagoLogo}
        alt="Mercado Pago"
      />
      <div>
        <span>Cuenta de cobro</span>
        <strong>{connected ? "Mercado Pago conectado" : "Conecta Mercado Pago"}</strong>
        <small>
          {connected
            ? connection.accountReference || "Lista para cobrar entregas con QR"
            : "Los QR de entrega se acreditan directamente en la cuenta del Rider."}
        </small>
      </div>
      <button
        type="button"
        className={connected ? "we-rider-secondary" : "we-rider-primary"}
        onClick={connected ? onDisconnect : onConnect}
        disabled={busy}
      >
        {busy ? "Procesando..." : connected ? "Desconectar" : "Conectar cuenta"}
      </button>
    </section>
  );
}

function ActiveDelivery({
  job,
  busy,
  paymentConnection,
  onAdvance,
  onVerifyPickup,
  onVerifyDelivery,
  onCreatePickupDeeplink,
  onRecordPickupCash,
  onRecordRestaurantDeliveryFeeCash,
  onCreateCollectionQr,
  onRecordCollectionCash,
  onRefreshPayment,
}) {
  const status = JOB_STATUS[job.status] || JOB_STATUS.driver_assigned;
  const action = ACTIVE_ACTIONS[job.status] || null;
  const [pickupCode, setPickupCode] = useState("");
  const [deliveryCode, setDeliveryCode] = useState("");
  const [pickupOperation, setPickupOperation] = useState(
    job.payment?.pickupSettlement || null
  );
  const [restaurantDeliveryFeeOperation, setRestaurantDeliveryFeeOperation] =
    useState(job.payment?.restaurantDeliveryFeeCollection || null);
  const [collectionOperation, setCollectionOperation] = useState(
    job.payment?.customerCollection || null
  );
  const [clock, setClock] = useState(Date.now());
  const pickupDeadline = job.pickupDeadlineAt
    ? new Date(job.pickupDeadlineAt).getTime()
    : null;
  const pickupWindowActive = [
    "driver_assigned",
    "arriving_pickup",
    "ready_for_pickup",
  ].includes(job.status);
  const pickupExpired = Boolean(
    pickupWindowActive && pickupDeadline && pickupDeadline <= clock
  );
  const pickupAttempts = Number(job.pickupVerificationAttempts || 0);
  const deliveryAttempts = Number(job.deliveryVerificationAttempts || 0);
  const pickupReady =
    !job.orderStatus ||
    ["ready_for_delivery", "assigned_to_driver", "out_for_delivery"].includes(
      job.orderStatus
    );

  useEffect(() => {
    if (job.payment?.pickupSettlement) {
      setPickupOperation(job.payment.pickupSettlement);
    }
    if (job.payment?.restaurantDeliveryFeeCollection) {
      setRestaurantDeliveryFeeOperation(
        job.payment.restaurantDeliveryFeeCollection
      );
    }
    if (job.payment?.customerCollection) {
      setCollectionOperation(job.payment.customerCollection);
    }
  }, [
    job.id,
    job.payment?.customerCollection,
    job.payment?.pickupSettlement,
    job.payment?.restaurantDeliveryFeeCollection,
  ]);

  useEffect(() => {
    const pendingOperation =
      pickupOperation?.status === "pending"
        ? { type: "pickup_settlement", setter: setPickupOperation }
        : restaurantDeliveryFeeOperation?.status === "pending"
          ? {
              type: "restaurant_delivery_fee_collection",
              setter: setRestaurantDeliveryFeeOperation,
            }
        : collectionOperation?.status === "pending"
          ? { type: "customer_collection", setter: setCollectionOperation }
          : null;
    if (!pendingOperation) return undefined;
    const intervalId = window.setInterval(() => {
      onRefreshPayment(job.id, pendingOperation.type).then((operation) => {
        if (operation) pendingOperation.setter(operation);
      });
    }, 4000);
    return () => window.clearInterval(intervalId);
  }, [
    collectionOperation?.status,
    job.id,
    onRefreshPayment,
    pickupOperation?.status,
    restaurantDeliveryFeeOperation?.status,
  ]);

  const orderPrepaid =
    job.payment?.status === "paid" && job.payment?.collectOnDelivery !== true;
  const prepaidOperation = orderPrepaid
    ? { status: "approved", method: "prepaid" }
    : null;
  const deliveryFeeRequired =
    orderPrepaid && Number(job.payment?.deliveryFee || 0) > 0;
  const freeDeliveryOperation =
    orderPrepaid && !deliveryFeeRequired
      ? { status: "approved", method: "free" }
      : null;
  const pickupPaid = orderPrepaid
    ? !deliveryFeeRequired ||
      restaurantDeliveryFeeOperation?.status === "approved"
    : pickupOperation?.status === "approved";
  const customerPaid = collectionOperation?.status === "approved" || orderPrepaid;

  useEffect(() => {
    if (!pickupDeadline || !pickupWindowActive) return undefined;
    setClock(Date.now());
    const intervalId = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [pickupDeadline, pickupWindowActive]);

  return (
    <section className="we-rider-active-delivery" aria-labelledby="we-active-delivery-title">
      <header className="we-rider-active-heading">
        <div>
          <span className={`we-rider-status is-${status.tone}`}>{status.label}</span>
          <h2 id="we-active-delivery-title">Pedido {job.displayNumber}</h2>
          <p>Actualizado {job.updatedAt ? timeFormatter.format(new Date(job.updatedAt)) : "ahora"}</p>
        </div>
        <div className="we-rider-active-total">
          <span>Total</span>
          <strong>{moneyFormatter.format(job.payment?.amount || 0)}</strong>
        </div>
      </header>

      <DeliveryProgress status={job.status} />

      {pickupDeadline && pickupWindowActive ? (
        <div className={`we-rider-pickup-deadline${pickupExpired ? " is-expired" : ""}`}>
          <div>
            <span>{pickupExpired ? "Tiempo vencido" : "Tiempo para llegar y retirar"}</span>
            <strong>
              {pickupExpired
                ? "El restaurante debe reasignar el viaje"
                : formatRemainingTime(pickupDeadline - clock)}
            </strong>
          </div>
          <small>
            {pickupExpired
              ? "No retires el pedido sin una nueva asignación."
              : "Al llegar, confirma tu llegada y pide el código de seis números."}
          </small>
        </div>
      ) : null}

      <div className="we-rider-active-layout">
        <div className="we-rider-route-details">
          <section className="we-rider-route-stop">
            <span className="we-rider-stop-marker is-pickup">A</span>
            <div>
              <small>Retirar en</small>
              <h3>{job.pickup?.name || "Restaurante"}</h3>
              <p>{job.pickup?.address || job.pickup?.city || "Dirección pendiente"}</p>
              <MapButton place={job.pickup}>Ir al restaurante</MapButton>
            </div>
          </section>
          <section className="we-rider-route-stop">
            <span className="we-rider-stop-marker is-delivery">B</span>
            <div>
              <small>Entregar a {job.customer?.name || "Cliente"}</small>
              <h3>{job.destination?.address || job.destination?.city || "Dirección pendiente"}</h3>
              {job.destination?.reference ? <p>{job.destination.reference}</p> : null}
              <div className="we-rider-contact-actions">
                <MapButton place={job.destination}>Ir al domicilio</MapButton>
                {job.customer?.phone ? (
                  <a className="we-rider-call-button" href={`tel:${job.customer.phone}`}>
                    Llamar
                  </a>
                ) : null}
              </div>
            </div>
          </section>
        </div>

        <aside className="we-rider-order-detail">
          <div>
            <span>Pedido</span>
            <JobItems items={job.items} />
          </div>
          <div>
            <span>Forma de pago</span>
            <strong>{PAYMENT_METHOD_LABELS[job.payment?.method] || "Informada"}</strong>
            {job.payment?.collectOnDelivery ? (
              <p className="we-rider-collect-alert">
                Cobrar {moneyFormatter.format(job.payment?.amount || 0)} al entregar.
              </p>
            ) : (
              <p className="we-rider-paid-alert">
                {Number(job.payment?.deliveryFee || 0) > 0
                  ? `El pedido ya está pagado. Cobra ${moneyFormatter.format(
                      job.payment?.deliveryFee || 0
                    )} de envio en la caja del restaurante.`
                  : "El pedido ya está pagado y el envio no tiene cargo."}
              </p>
            )}
          </div>
          {job.notes ? (
            <div><span>Indicaciones</span><p>{job.notes}</p></div>
          ) : null}
        </aside>
      </div>

      <footer className="we-rider-active-footer">
        {job.status === "arrived_pickup" ? (
          <div className="we-rider-payment-and-code">
            <RiderPaymentStep
              kind={orderPrepaid ? "restaurant_delivery_fee" : "pickup"}
              amount={
                orderPrepaid
                  ? job.payment?.deliveryFee
                  : job.payment?.subtotal
              }
              operation={
                orderPrepaid
                  ? restaurantDeliveryFeeOperation || freeDeliveryOperation
                  : pickupOperation
              }
              mercadoPagoConnected={paymentConnection?.connected}
              busy={busy}
              onCash={async () => {
                if (orderPrepaid) {
                  const operation = await onRecordRestaurantDeliveryFeeCash(
                    job.id
                  );
                  if (operation) {
                    setRestaurantDeliveryFeeOperation(operation);
                  }
                  return;
                }
                const operation = await onRecordPickupCash(job.id);
                if (operation) setPickupOperation(operation);
              }}
              onDigital={
                orderPrepaid
                  ? undefined
                  : async () => {
                      const operation = await onCreatePickupDeeplink(job.id);
                      if (operation) setPickupOperation(operation);
                    }
              }
              onRefresh={async () => {
                const operationType = orderPrepaid
                  ? "restaurant_delivery_fee_collection"
                  : "pickup_settlement";
                const operation = await onRefreshPayment(
                  job.id,
                  operationType
                );
                if (orderPrepaid) {
                  if (operation) setRestaurantDeliveryFeeOperation(operation);
                } else if (operation) {
                  setPickupOperation(operation);
                }
              }}
              onApproveLocal={() => {
                const operationType = orderPrepaid
                  ? "restaurant_delivery_fee_collection"
                  : "pickup_settlement";
                const operation = approveLocalWeRiderPayment(
                  job.id,
                  operationType
                );
                if (orderPrepaid) {
                  if (operation) setRestaurantDeliveryFeeOperation(operation);
                } else if (operation) {
                  setPickupOperation(operation);
                }
              }}
            />
            {!pickupPaid ? null : !pickupReady ? (
          <div className="we-rider-pickup-waiting" role="status">
            <strong>Esperando a Cocina</strong>
            <span>
              Ya registramos tu llegada. El código se habilitará automáticamente
              cuando el restaurante marque el pedido como listo.
            </span>
          </div>
            ) : (
          <form
            className="we-rider-pickup-verification"
            onSubmit={(event) => {
              event.preventDefault();
              onVerifyPickup(job.id, pickupCode);
            }}
          >
            <label htmlFor={`pickup-code-${job.id}`}>
              Código de retiro
              <span>El restaurante te muestra seis números para autorizar la entrega.</span>
            </label>
            <div>
              <input
                id={`pickup-code-${job.id}`}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={pickupCode}
                disabled={busy || pickupExpired || job.pickupVerificationLocked}
                onChange={(event) =>
                  setPickupCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="000000"
                aria-label="Código de retiro de seis números"
              />
              <button
                className="we-rider-primary"
                type="submit"
                disabled={
                  busy ||
                  pickupExpired ||
                  job.pickupVerificationLocked ||
                  pickupCode.length !== 6
                }
              >
                {busy ? "Verificando..." : "Verificar y retirar"}
              </button>
            </div>
            {pickupAttempts > 0 ? (
              <small>
                Intentos disponibles: {Math.max(0, 5 - pickupAttempts)}
              </small>
            ) : null}
          </form>
            )}
          </div>
        ) : job.status === "in_transit" ? (
          <div className="we-rider-payment-and-code">
            <RiderPaymentStep
              kind="collection"
              amount={job.payment?.amount}
              operation={collectionOperation || prepaidOperation}
              mercadoPagoConnected={paymentConnection?.connected}
              busy={busy}
              onCash={async () => {
                const operation = await onRecordCollectionCash(job.id);
                if (operation) setCollectionOperation(operation);
              }}
              onDigital={async () => {
                const operation = await onCreateCollectionQr(job.id);
                if (operation) setCollectionOperation(operation);
              }}
              onRefresh={async () => {
                const operation = await onRefreshPayment(job.id, "customer_collection");
                if (operation) setCollectionOperation(operation);
              }}
              onApproveLocal={() => {
                const operation = approveLocalWeRiderPayment(
                  job.id,
                  "customer_collection"
                );
                if (operation) setCollectionOperation(operation);
              }}
            />
            {customerPaid ? (
          <form
            className="we-rider-pickup-verification we-rider-delivery-verification"
            onSubmit={(event) => {
              event.preventDefault();
              onVerifyDelivery(job.id, deliveryCode);
            }}
          >
            <label htmlFor={`delivery-code-${job.id}`}>
              Código de entrega
              <span>
                Pídele al cliente los seis números y verifícalos con el pedido en sus manos.
              </span>
            </label>
            <div>
              <input
                id={`delivery-code-${job.id}`}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={deliveryCode}
                disabled={busy || job.deliveryVerificationLocked}
                onChange={(event) =>
                  setDeliveryCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="000000"
                aria-label="Código de entrega de seis números"
              />
              <button
                className="we-rider-primary is-delivery-confirm"
                type="submit"
                disabled={
                  busy ||
                  job.deliveryVerificationLocked ||
                  deliveryCode.length !== 6
                }
              >
                {busy ? "Verificando..." : "Verificar y entregar"}
              </button>
            </div>
            {deliveryAttempts > 0 ? (
              <small>
                Intentos disponibles: {Math.max(0, 5 - deliveryAttempts)}
              </small>
            ) : null}
          </form>
            ) : null}
          </div>
        ) : job.status === "arriving_pickup" ? (
          <div className="we-rider-waiting-note">
            <span aria-hidden="true">●</span>
            Cuando estés en el local, confirma tu llegada para validar el retiro.
          </div>
        ) : <span />}
        {action ? (
          <button
            className={`we-rider-primary${action.nextStatus === "delivered" ? " is-delivery-confirm" : ""}`}
            type="button"
            disabled={busy || pickupExpired}
            onClick={() => onAdvance(job.id, action.nextStatus)}
          >
            {busy ? "Actualizando..." : action.label}
          </button>
        ) : null}
      </footer>

    </section>
  );
}

export default function WeRider() {
  useWePedidosBranding("Rider", true, weRiderLogo);
  const [auth, setAuth] = useState({ loading: true, session: null, profile: null });
  const [dashboard, setDashboard] = useState(null);
  const [paymentConnection, setPaymentConnection] = useState({
    connected: false,
    status: "not_connected",
    accountReference: "",
  });
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [error, setError] = useState("");
  const [authIssue, setAuthIssue] = useState("");
  const [locationState, setLocationState] = useState("idle");
  const [connectionState, setConnectionState] = useState(() =>
    typeof navigator !== "undefined" && navigator.onLine === false
      ? "offline"
      : "online"
  );
  const refreshingRef = useRef(false);
  const refreshPendingRef = useRef(false);
  const lastLocationSentRef = useRef(0);
  const dashboardFailuresRef = useRef(0);
  const offlineTimerRef = useRef(null);
  const notificationDashboardRef = useRef(null);
  const nativeBackgroundRegistrationRef = useRef("");

  const refreshAuth = useCallback(async () => {
    try {
      const nextAuth = await getWeRiderAuthState();
      setAuthIssue("");
      setAuth({ loading: false, ...nextAuth });
      if (!nextAuth.session) {
        notificationDashboardRef.current = null;
        setDashboard(null);
      }
      return nextAuth;
    } catch (requestError) {
      setAuth((currentAuth) =>
        currentAuth.session
          ? { ...currentAuth, loading: false }
          : { loading: false, session: null, profile: null }
      );
      setAuthIssue(requestError?.message || "No pudimos recuperar tu sesión.");
      return null;
    }
  }, []);

  const refreshDashboard = useCallback(async ({ silent = false } = {}) => {
    if (refreshingRef.current) {
      refreshPendingRef.current = true;
      return null;
    }
    refreshingRef.current = true;
    if (!silent) setLoadingDashboard(true);
    try {
      const nextDashboard = await loadWeRiderDashboard();
      dashboardFailuresRef.current = 0;
      setConnectionState("online");
      setAuthIssue("");
      notifyRiderDashboardChanges(notificationDashboardRef.current, nextDashboard);
      notificationDashboardRef.current = nextDashboard;
      setDashboard(nextDashboard);
      if (nextDashboard?.paymentConnection) {
        setPaymentConnection(nextDashboard.paymentConnection);
      }
      setError("");
      return nextDashboard;
    } catch (requestError) {
      dashboardFailuresRef.current += 1;
      if (
        (typeof navigator !== "undefined" && navigator.onLine === false) ||
        dashboardFailuresRef.current >= 3
      ) {
        setConnectionState("reconnecting");
      }
      if (!silent) {
        setError(requestError?.message || "No pudimos cargar los viajes.");
      }
      return null;
    } finally {
      refreshingRef.current = false;
      if (!silent) setLoadingDashboard(false);
      if (refreshPendingRef.current) {
        refreshPendingRef.current = false;
        window.setTimeout(() => {
          void refreshDashboard({ silent: true });
        }, 0);
      }
    }
  }, []);

  useEffect(() => {
    refreshAuth();
    return subscribeToWeRiderAuth(() => refreshAuth());
  }, [refreshAuth]);

  useEffect(() => {
    if (!auth.session) return undefined;
    let active = true;
    getWeRiderPaymentConnection()
      .then((connection) => {
        if (active) setPaymentConnection(connection);
      })
      .catch((requestError) => {
        if (active) {
          setError(
            requestError?.message || "No pudimos consultar Mercado Pago."
          );
        }
      });
    return () => {
      active = false;
    };
  }, [auth.session]);

  useEffect(() => {
    const markOnline = () => {
      if (offlineTimerRef.current !== null) {
        window.clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
      dashboardFailuresRef.current = 0;
      setConnectionState("online");
      if (auth.session) void refreshDashboard({ silent: true });
    };
    const markOfflineAfterGracePeriod = () => {
      if (offlineTimerRef.current !== null) return;
      offlineTimerRef.current = window.setTimeout(() => {
        offlineTimerRef.current = null;
        if (navigator.onLine === false) setConnectionState("offline");
      }, 8000);
    };

    window.addEventListener("online", markOnline);
    window.addEventListener("offline", markOfflineAfterGracePeriod);
    return () => {
      window.removeEventListener("online", markOnline);
      window.removeEventListener("offline", markOfflineAfterGracePeriod);
      if (offlineTimerRef.current !== null) {
        window.clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
    };
  }, [auth.session, refreshDashboard]);

  useEffect(() => {
    if (!auth.session) return undefined;
    refreshDashboard();
    const intervalId = window.setInterval(
      () => refreshDashboard({ silent: true }),
      4000
    );
    const refreshWhenVisible = () => {
      if (!document.hidden) void refreshDashboard({ silent: true });
    };
    const unsubscribeRiderUpdates = subscribeToWeRiderDashboardChanges(
      auth.session?.user?.id,
      () => {
        void refreshDashboard({ silent: true });
      }
    );
    window.addEventListener("focus", refreshWhenVisible);
    window.addEventListener("pageshow", refreshWhenVisible);
    window.addEventListener("online", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshWhenVisible);
      window.removeEventListener("pageshow", refreshWhenVisible);
      window.removeEventListener("online", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      unsubscribeRiderUpdates();
    };
  }, [auth.session, refreshDashboard]);

  const riderStatus = dashboard?.profile?.status || auth.profile?.status || "offline";
  const locationEnabled = ["available", "delivering"].includes(riderStatus);
  const hasRiderSession = Boolean(auth.session);

  useEffect(() => {
    const riderId = auth.session?.user?.id || dashboard?.profile?.userId || "";
    if (!hasRiderSession || !locationEnabled) {
      nativeBackgroundRegistrationRef.current = "";
      stopNativeRiderBackground();
      return undefined;
    }

    const registrationKey = `${riderId}:${riderStatus}`;
    if (nativeBackgroundRegistrationRef.current === registrationKey) {
      return undefined;
    }
    nativeBackgroundRegistrationRef.current = registrationKey;
    let cancelled = false;
    registerWeRiderBackgroundDevice()
      .then((configuration) => {
        if (!cancelled && configuration) {
          configureNativeRiderBackground(configuration);
        }
      })
      .catch(() => {
        if (!cancelled) nativeBackgroundRegistrationRef.current = "";
      });

    return () => {
      cancelled = true;
    };
  }, [
    auth.session?.user?.id,
    dashboard?.profile?.userId,
    hasRiderSession,
    locationEnabled,
    riderStatus,
  ]);

  useEffect(() => {
    if (!auth.session || !locationEnabled || !navigator.geolocation) {
      setLocationState(locationEnabled ? "unsupported" : "idle");
      return undefined;
    }

    setLocationState("requesting");
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setLocationState("active");
        const now = Date.now();
        if (now - lastLocationSentRef.current < 15000) return;
        lastLocationSentRef.current = now;
        updateWeRiderLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }).catch(() => setLocationState("error"));
      },
      () => setLocationState("denied"),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [auth.session, locationEnabled]);

  const profile = dashboard?.profile || auth.profile;
  const compliance = normalizeWeRiderCompliance(dashboard?.compliance);
  const canOperate = compliance.canOperate;
  const requiresIdentitySubmission = [
    "not_started",
    "action_required",
    "rejected",
    "expired",
  ].includes(compliance.identityStatus);
  const online = profile?.status === "available" || profile?.status === "delivering";
  const activeJob = dashboard?.activeJob || null;
  const availableJobs = dashboard?.availableJobs || [];

  const locationLabel = useMemo(() => {
    if (!online) return "Ubicación detenida";
    if (locationState === "active") return "Ubicación activa";
    if (locationState === "denied") return "Habilita la ubicación";
    if (locationState === "unsupported") return "Ubicación no disponible";
    if (locationState === "error") return "Reintentando ubicación";
    return "Buscando ubicación";
  }, [locationState, online]);

  const runDashboardAction = async (actionKey, request) => {
    setBusyAction(actionKey);
    setError("");
    try {
      const nextDashboard = await request();
      notificationDashboardRef.current = nextDashboard;
      setDashboard(nextDashboard);
      return nextDashboard;
    } catch (requestError) {
      setError(requestError?.message || "No pudimos completar la operación.");
      await refreshDashboard({ silent: true });
      return null;
    } finally {
      setBusyAction("");
    }
  };

  const runPaymentAction = useCallback(async (actionKey, request) => {
    setBusyAction(actionKey);
    setError("");
    try {
      const operation = await request();
      return operation;
    } catch (requestError) {
      setError(requestError?.message || "No pudimos completar el pago.");
      return null;
    } finally {
      setBusyAction("");
    }
  }, []);

  const handleRefreshPayment = useCallback(
    (assignmentId, operationType) =>
      runPaymentAction(`payment-refresh-${assignmentId}`, () =>
        refreshWeRiderPaymentOperation(assignmentId, operationType)
      ),
    [runPaymentAction]
  );

  const handleMercadoPagoConnection = async () => {
    setBusyAction("mercado-pago-connect");
    setError("");
    try {
      const result = await startWeRiderMercadoPagoConnection();
      if (result.local) {
        setPaymentConnection(result.connection);
      } else if (result.authorizationUrl) {
        window.location.assign(result.authorizationUrl);
      }
    } catch (requestError) {
      setError(requestError?.message || "No pudimos conectar Mercado Pago.");
    } finally {
      setBusyAction("");
    }
  };

  const handleMercadoPagoDisconnect = async () => {
    setBusyAction("mercado-pago-connect");
    setError("");
    try {
      setPaymentConnection(await disconnectWeRiderMercadoPago());
    } catch (requestError) {
      setError(requestError?.message || "No pudimos desconectar Mercado Pago.");
    } finally {
      setBusyAction("");
    }
  };

  const handleAvailability = async (event) => {
    const nextStatus = event.target.checked ? "available" : "offline";
    if (nextStatus === "available" && !canOperate) {
      setError("Completa la validación de identidad antes de conectarte.");
      return;
    }
    const nextDashboard = await runDashboardAction("availability", () =>
      setWeRiderAvailability(nextStatus)
    );
    if (nextStatus === "offline" && nextDashboard) {
      stopNativeRiderBackground();
      nativeBackgroundRegistrationRef.current = "";
      void revokeWeRiderBackgroundDevice().catch(() => {});
    }
  };

  const handleLogout = async () => {
    setBusyAction("logout");
    try {
      await revokeWeRiderBackgroundDevice().catch(() => null);
      stopNativeRiderBackground();
      nativeBackgroundRegistrationRef.current = "";
      await signOutWeRider();
      notificationDashboardRef.current = null;
      setDashboard(null);
      await refreshAuth();
    } catch (requestError) {
      setError(requestError?.message || "No pudimos cerrar la sesión.");
    } finally {
      setBusyAction("");
    }
  };

  if (auth.loading) {
    return (
      <main className="we-rider-loading">
        <img src={weRiderLogo} alt="We! Rider" />
        <p>Cargando tu turno...</p>
      </main>
    );
  }

  if (authIssue && !auth.session) {
    return (
      <main className="we-rider-loading">
        <img src={weRiderLogo} alt="We! Rider" />
        <p>{authIssue}</p>
        <button
          className="we-rider-primary"
          type="button"
          onClick={() => {
            setAuth({ loading: true, session: null, profile: null });
            void refreshAuth();
          }}
        >
          Reintentar
        </button>
      </main>
    );
  }

  if (!auth.session) {
    return <WeRiderAccess onAuthenticated={refreshAuth} />;
  }

  if (dashboard?.needsProfile) {
    return (
      <RiderProfileCompletion
        email={auth.session?.user?.email || ""}
        onSaved={setDashboard}
      />
    );
  }

  if (dashboard && profile && requiresIdentitySubmission) {
    return (
      <WeRiderIdentityOnboarding
        profile={profile}
        compliance={compliance}
        onSubmit={async (submission) => {
          const nextDashboard = await submitWeRiderIdentity(submission);
          notificationDashboardRef.current = nextDashboard;
          setDashboard(nextDashboard);
          return nextDashboard;
        }}
        onUpdateProfile={async (profileChanges) => {
          const nextDashboard = await registerWeRiderProfile(profileChanges);
          notificationDashboardRef.current = nextDashboard;
          setDashboard(nextDashboard);
          return nextDashboard;
        }}
        onSignOut={handleLogout}
      />
    );
  }

  return (
    <main className="we-rider-shell">
      <header className="we-rider-header">
        <a className="we-rider-brand" href="/we-pedidos/rider" aria-label="We! Rider">
          <img src={weRiderLogo} alt="" />
          <span><strong>We! Rider</strong><small>Entregas</small></span>
        </a>
        <div className="we-rider-header-actions">
          <WeRiderAndroidLauncher compact />
          <div className={`we-rider-location-state is-${locationState}`}>
            <span aria-hidden="true">●</span>{locationLabel}
          </div>
          <button
            className="we-rider-icon-button"
            type="button"
            title="Actualizar viajes"
            aria-label="Actualizar viajes"
            disabled={loadingDashboard}
            onClick={() => refreshDashboard()}
          >
            ↻
          </button>
          <button
            className="we-rider-profile-button"
            type="button"
            onClick={handleLogout}
            disabled={busyAction === "logout"}
            title="Cerrar sesión"
          >
            <span>{getInitials(profile?.fullName)}</span>
            <div><strong>{profile?.fullName || "Rider"}</strong><small>Cerrar sesión</small></div>
          </button>
        </div>
      </header>

      {connectionState !== "online" ? (
        <div className="we-rider-connection-banner" role="status">
          <span aria-hidden="true">●</span>
          <div>
            <strong>Reconectando</strong>
            <small>Tu viaje sigue visible. Reintentaremos automáticamente.</small>
          </div>
        </div>
      ) : null}

      {authIssue && auth.session ? (
        <div className="we-rider-connection-banner" role="status">
          <span aria-hidden="true">●</span>
          <div>
            <strong>Recuperando tu sesión</strong>
            <small>Conservamos la entrega mientras vuelve la conexión.</small>
          </div>
        </div>
      ) : null}

      <section className="we-rider-shift-bar">
        <div>
          <span className="we-rider-eyebrow">Turno actual</span>
          <h1>{activeJob ? "Entrega en curso" : online ? "Disponible para viajar" : "Estás desconectado"}</h1>
          <p>
            {VEHICLE_LABELS[profile?.vehicleType] || "Vehículo"}
            {profile?.vehicleReference ? ` · ${profile.vehicleReference}` : ""}
          </p>
        </div>
        <label className={`we-rider-availability${online ? " is-online" : ""}`}>
          <span>{online ? "Disponible" : "No disponible"}</span>
          <input
            type="checkbox"
            checked={online}
            onChange={handleAvailability}
            disabled={
              Boolean(activeJob) ||
              busyAction === "availability" ||
              (!online && !canOperate)
            }
          />
          <i aria-hidden="true" />
        </label>
      </section>

      <RiderComplianceSummary compliance={compliance} />

      <RiderMercadoPagoCard
        connection={paymentConnection}
        busy={busyAction === "mercado-pago-connect"}
        onConnect={handleMercadoPagoConnection}
        onDisconnect={handleMercadoPagoDisconnect}
      />

      {error ? (
        <div className="we-rider-global-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => setError("")} aria-label="Cerrar">×</button>
        </div>
      ) : null}

      <div className="we-rider-main">
        {activeJob ? (
          <ActiveDelivery
            job={activeJob}
            busy={
              busyAction === `advance-${activeJob.id}` ||
              busyAction === `pickup-${activeJob.id}` ||
              busyAction === `delivery-${activeJob.id}` ||
              (busyAction.includes(activeJob.id) &&
                busyAction.startsWith("payment-"))
            }
            paymentConnection={paymentConnection}
            onAdvance={(assignmentId, nextStatus) =>
              runDashboardAction(`advance-${assignmentId}`, () =>
                advanceWeRiderJob(assignmentId, nextStatus)
              )
            }
            onVerifyPickup={(assignmentId, code) =>
              runDashboardAction(`pickup-${assignmentId}`, () =>
                verifyWeRiderPickupCode(assignmentId, code)
              )
            }
            onVerifyDelivery={(assignmentId, code) =>
              runDashboardAction(`delivery-${assignmentId}`, () =>
                verifyWeRiderDeliveryCode(assignmentId, code)
              )
            }
            onCreatePickupDeeplink={(assignmentId) =>
              runPaymentAction(`payment-pickup-link-${assignmentId}`, async () => {
                const quote = await quoteWeRiderPickupPayment(assignmentId, "deeplink");
                if (quote.hasCashPromotion && !window.confirm(
                  `El restaurante cobrara por Mercado Pago, sin la oferta en efectivo.\nAl restaurante: ${moneyFormatter.format(quote.subtotal)}.\nTotal a cobrar al cliente, con envio: ${moneyFormatter.format(quote.total)}.\n\nConfirma que informaste este importe al cliente y que lo acepto antes de continuar.`
                )) return null;
                return createWeRiderPickupDeeplink(assignmentId, { expectedTotal: quote.total, customerPriceConfirmed: true });
              })
            }
            onRecordPickupCash={(assignmentId) =>
              runPaymentAction(`payment-pickup-cash-${assignmentId}`, async () => {
                const quote = await quoteWeRiderPickupPayment(assignmentId, "cash");
                if (quote.hasCashPromotion && !window.confirm(
                  `Pago en efectivo al restaurante: ${moneyFormatter.format(quote.subtotal)}.\nTotal a cobrar al cliente, con envio: ${moneyFormatter.format(quote.total)}.\n\nConfirmar efectivo entregado?`
                )) return null;
                return recordWeRiderPickupCash(assignmentId, { expectedTotal: quote.total, customerPriceConfirmed: true });
              })
            }
            onRecordRestaurantDeliveryFeeCash={(assignmentId) =>
              runPaymentAction(
                `payment-restaurant-delivery-fee-${assignmentId}`,
                () => recordWeRiderRestaurantDeliveryFeeCash(assignmentId)
              )
            }
            onCreateCollectionQr={(assignmentId) =>
              runPaymentAction(`payment-collection-qr-${assignmentId}`, () =>
                createWeRiderCollectionQr(assignmentId)
              )
            }
            onRecordCollectionCash={(assignmentId) =>
              runPaymentAction(`payment-collection-cash-${assignmentId}`, () =>
                recordWeRiderCollectionCash(assignmentId)
              )
            }
            onRefreshPayment={handleRefreshPayment}
          />
        ) : (
          <section className="we-rider-jobs-section" aria-labelledby="we-rider-jobs-title">
            <header>
              <div>
                <p className="we-rider-eyebrow">Nuevas entregas</p>
                <h2 id="we-rider-jobs-title">Viajes disponibles</h2>
              </div>
              <span>{availableJobs.length}</span>
            </header>

            {!online ? (
              <div className="we-rider-empty-state">
                <img src={weRiderLogo} alt="" />
                <h3>Activa tu disponibilidad</h3>
                <p>Al conectarte verás los pedidos listos para asignación.</p>
                <button
                  className="we-rider-primary"
                  type="button"
                  onClick={() =>
                    runDashboardAction("availability", () =>
                      setWeRiderAvailability("available")
                    )
                  }
                  disabled={busyAction === "availability" || !canOperate}
                >
                  {canOperate ? "Conectarme" : "Habilitación pendiente"}
                </button>
              </div>
            ) : loadingDashboard && !dashboard ? (
              <div className="we-rider-empty-state"><p>Buscando viajes...</p></div>
            ) : availableJobs.length ? (
              <div className="we-rider-jobs-grid">
                {availableJobs.map((job) => (
                  <AvailableJobCard
                    key={job.id}
                    job={job}
                    online={online && canOperate}
                    busy={busyAction === `claim-${job.id}`}
                    onAccept={(assignmentId) =>
                      runDashboardAction(`claim-${assignmentId}`, () =>
                        claimWeRiderJob(assignmentId)
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="we-rider-empty-state">
              <img src={weRiderLogo} alt="" />
                <h3>No hay viajes disponibles</h3>
                <p>La lista se actualiza automáticamente.</p>
              </div>
            )}
          </section>
        )}
      </div>
    </main>
  );
}
